import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, AlarmClock, ListTodo, CheckCircle2, Upload, ClipboardList, CalendarDays, MessageSquare, FolderOpen } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { CASE_STATUS_PROGRESS } from "../data/content";
import { Button } from "../components/ui/button";

export default function PortalDashboard() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [journeySteps, setJourneySteps] = useState([]);
  const [intake, setIntake] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [threads, setThreads] = useState([]);
  const [docRequests, setDocRequests] = useState([]);

  useEffect(() => {
    api.get("/cases/mine").then((r) => setCases(r.data)).catch(() => {});
    api.get("/deadlines").then((r) => setDeadlines(r.data)).catch(() => {});
    api.get("/tasks").then((r) => setTasks(r.data)).catch(() => {});
    api.get("/meta/options").then((r) => { setStatuses(r.data.case_statuses); setJourneySteps(r.data.journey_steps); }).catch(() => {});
    api.get("/client-intakes/current").then((r) => setIntake(r.data)).catch(() => {});
    api.get("/appointments").then((r) => setAppointments(r.data)).catch(() => {});
    api.get("/messages/threads").then((r) => setThreads(r.data)).catch(() => {});
    api.get("/doc-requests").then((r) => setDocRequests(r.data)).catch(() => {});
  }, []);

  const currentStep = user?.journey_step ?? 0;
  const readOnlyIntakeDone = intake && ["SUBMITTED", "REVIEWED"].includes(intake.status);
  const today = new Date().toISOString().slice(0, 10);
  const nextAppt = appointments.filter((a) => a.date >= today && a.status !== "CANCELLED").sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time))[0];
  const lastThread = threads[0];
  const docsNeeded = docRequests.flatMap((r) => r.items).filter((i) => ["REQUESTED", "NEEDS REPLACEMENT"].includes(i.status));
  const MODE_LABELS = { in_person: "In person", virtual: "Video call", phone: "Phone" };
  const openTasks = tasks.filter((t) => t.status !== "COMPLETED");
  const upcomingDeadlines = deadlines.filter((d) => d.date >= new Date().toISOString().slice(0, 10)).slice(0, 4);

  return (
    <div className="space-y-8" data-testid="portal-dashboard">
      <div>
        <p className="text-xs font-sans font-bold tracking-[0.15em] uppercase text-teal">Welcome, {user?.name?.split(" ")[0]}</p>
        <h1 className="text-2xl sm:text-3xl font-serif font-semibold text-navy tracking-tight mt-1">Your Full Circle Journey</h1>
      </div>

      {/* Intake call-to-action */}
      {intake && ["SENT", "IN PROGRESS"].includes(intake.status) && (
        <Link
          to="/portal/intake"
          data-testid="dashboard-intake-cta"
          className="flex items-center gap-4 bg-teal-light border border-teal/30 rounded-2xl p-5 hover:border-teal transition-colors group"
        >
          <div className="h-11 w-11 rounded-xl bg-teal flex items-center justify-center flex-shrink-0">
            <ClipboardList className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-navy">Complete your secure intake</p>
            <p className="text-xs text-mutedink mt-0.5">Your team needs this to build your Journey Plan. About 10–15 minutes — you can save and finish later.</p>
          </div>
          <ArrowRight className="h-5 w-5 text-teal group-hover:translate-x-1 transition-transform" />
        </Link>
      )}

      {/* Journey progress */}
      <section className="bg-surface border border-line rounded-2xl p-6 shadow-sm" data-testid="journey-progress-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-sans font-semibold text-navy">Journey progress</h2>
          <span className="text-xs font-medium text-teal">Step {Math.min(currentStep + 1, 9)} of 9</span>
        </div>
        <ol className="space-y-3">
          {journeySteps.map((s, i) => (
            <li key={s} className="flex items-center gap-3">
              <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 ${i < currentStep ? "bg-sage text-white" : i === currentStep ? "bg-teal text-white" : "bg-sand text-mutedink"}`}>
                {i < currentStep ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
              </span>
              {i === 1 && !readOnlyIntakeDone ? (
                <Link to="/portal/intake" className={`text-sm underline decoration-teal/50 underline-offset-4 hover:text-teal transition-colors ${i === currentStep ? "text-navy font-semibold" : "text-mutedink"}`} data-testid="journey-step-intake-link">{s}</Link>
              ) : (
                <span className={`text-sm ${i === currentStep ? "text-navy font-semibold" : "text-mutedink"}`}>{s}</span>
              )}
              {i < currentStep && <span className="text-[10px] font-bold text-sage uppercase tracking-wide">Done</span>}
            </li>
          ))}
        </ol>
      </section>

      {/* Services */}
      <section data-testid="journey-services">
        <h2 className="text-base font-sans font-semibold text-navy mb-4">Your services</h2>
        {cases.length === 0 ? (
          <div className="bg-surface border border-dashed border-line rounded-2xl p-8 text-center">
            <p className="text-sm text-mutedink mb-4">No active services yet. Start your journey and we will organize everything here.</p>
            <Button asChild className="bg-teal text-white hover:bg-teal-dark hover:text-white rounded-full" data-testid="dashboard-start-journey-button">
              <Link to="/start">Start Your Journey <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {cases.map((c) => {
              const pct = CASE_STATUS_PROGRESS(c.status, statuses);
              return (
                <div key={c.case_id} className="bg-surface border border-line rounded-xl p-5 shadow-sm" data-testid={`dashboard-case-${c.case_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-sans font-semibold text-navy">{c.service_type}</h3>
                    <span className="text-[11px] font-medium bg-teal-light text-teal px-2.5 py-1 rounded-full">{c.status}</span>
                  </div>
                  <div className="h-2 rounded-full bg-sand overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-teal to-sage transition-all" style={{ width: `${Math.max(pct, 4)}%` }} />
                  </div>
                  <p className="mt-2 text-xs text-mutedink">{pct}% complete</p>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* At-a-glance panels */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="dashboard-panels">
        <Link to="/portal/appointments" className="bg-surface border border-line rounded-2xl p-5 hover:border-teal transition-colors" data-testid="panel-appointment">
          <p className="text-xs font-bold tracking-[0.1em] uppercase text-teal flex items-center gap-1.5 mb-3"><CalendarDays className="h-3.5 w-3.5" /> Upcoming appointment</p>
          {nextAppt ? (
            <>
              <p className="text-sm font-semibold text-navy">{nextAppt.service_type}</p>
              <p className="text-xs text-mutedink mt-1">{nextAppt.date} at {nextAppt.time} · {MODE_LABELS[nextAppt.mode] || nextAppt.mode}</p>
              <span className="inline-block mt-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal-light text-teal">{nextAppt.status}</span>
            </>
          ) : (
            <p className="text-sm text-mutedink">None scheduled — book one anytime.</p>
          )}
        </Link>
        <Link to="/portal/messages" className="bg-surface border border-line rounded-2xl p-5 hover:border-teal transition-colors" data-testid="panel-message">
          <p className="text-xs font-bold tracking-[0.1em] uppercase text-teal flex items-center gap-1.5 mb-3"><MessageSquare className="h-3.5 w-3.5" /> Recent message</p>
          {lastThread ? (
            <>
              <p className="text-sm font-semibold text-navy truncate">{lastThread.subject}</p>
              <p className="text-xs text-mutedink mt-1 line-clamp-2">{lastThread.last_message}</p>
              {lastThread.unread > 0 && <span className="inline-block mt-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-champagne-light text-champagne-dark">{lastThread.unread} unread</span>}
            </>
          ) : (
            <p className="text-sm text-mutedink">No messages yet — your team is one tap away.</p>
          )}
        </Link>
        <Link to="/portal/documents" className="bg-surface border border-line rounded-2xl p-5 hover:border-teal transition-colors" data-testid="panel-docs-needed">
          <p className="text-xs font-bold tracking-[0.1em] uppercase text-teal flex items-center gap-1.5 mb-3"><FolderOpen className="h-3.5 w-3.5" /> Documents needed</p>
          {docsNeeded.length > 0 ? (
            <>
              <p className="text-2xl font-serif font-semibold text-champagne-dark">{docsNeeded.length}</p>
              <p className="text-xs text-mutedink mt-1 line-clamp-2">{docsNeeded.slice(0, 2).map((i) => i.name).join(" · ")}{docsNeeded.length > 2 && ` · +${docsNeeded.length - 2} more`}</p>
            </>
          ) : (
            <p className="text-sm text-mutedink">Nothing requested right now.</p>
          )}
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Deadlines */}
        <section className="bg-surface border border-line rounded-2xl p-6 shadow-sm" data-testid="dashboard-deadlines">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-sans font-semibold text-navy flex items-center gap-2"><AlarmClock className="h-4 w-4 text-champagne-dark" /> Upcoming dates</h2>
            <Link to="/portal/deadlines" className="text-xs font-medium text-teal hover:underline" data-testid="dashboard-deadlines-link">View all</Link>
          </div>
          {upcomingDeadlines.length === 0 ? (
            <p className="text-sm text-mutedink">No upcoming deadlines. We will post important dates here.</p>
          ) : (
            <ul className="space-y-3">
              {upcomingDeadlines.map((d) => (
                <li key={d.deadline_id} className="flex items-start justify-between gap-3 text-sm">
                  <span className="text-ink">{d.title}</span>
                  <span className="text-xs font-semibold text-champagne-dark whitespace-nowrap">{d.date}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Tasks */}
        <section className="bg-surface border border-line rounded-2xl p-6 shadow-sm" data-testid="dashboard-tasks">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-sans font-semibold text-navy flex items-center gap-2"><ListTodo className="h-4 w-4 text-teal" /> Your tasks</h2>
            <Link to="/portal/tasks" className="text-xs font-medium text-teal hover:underline" data-testid="dashboard-tasks-link">View all</Link>
          </div>
          {openTasks.length === 0 ? (
            <p className="text-sm text-mutedink">Nothing needed from you right now.</p>
          ) : (
            <ul className="space-y-3">
              {openTasks.slice(0, 4).map((t) => (
                <li key={t.task_id} className="flex items-start justify-between gap-3 text-sm">
                  <span className="text-ink">{t.title}</span>
                  {t.due_date && <span className="text-xs text-mutedink whitespace-nowrap">Due {t.due_date}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Quick actions */}
      <section className="bg-[#101315] border border-teal/25 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="dashboard-quick-actions">
        <div>
          <h2 className="text-lg font-serif font-semibold text-cream">Have a document ready?</h2>
          <p className="text-sm text-cream/70 mt-1">Upload it securely — from your phone camera or computer.</p>
        </div>
        <Button asChild className="bg-champagne text-espresso hover:bg-champagne-dark hover:text-espresso rounded-full" data-testid="dashboard-upload-button">
          <Link to="/portal/documents"><Upload className="mr-2 h-4 w-4" /> Upload Documents</Link>
        </Button>
      </section>
    </div>
  );
}
